/**
 * Tela de edição de proposta publicada.
 *
 * Carrega o documento do servidor por `GET /propostas/{id}`, extrai os valores do formulário a
 * partir das premissas gravadas, e abre o `TelaConstrutor` em modo edição. A separação em dois
 * componentes mantém o construtor puro: ele não conhece a rota nem a origem dos dados.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';

import type { DocumentoProposta } from '@emaster/dominio';

import { obterProposta } from '../api/cliente.ts';
import { TelaConstrutor, type PropsModoEdicao } from '../construtor/TelaConstrutor.tsx';
import type { ValoresFormulario } from '../construtor/montarDocumento.ts';

/** Extrai os valores do formulário a partir de um documento já publicado. */
function documentoParaValores(doc: DocumentoProposta): ValoresFormulario {
  const p = doc.premissas;

  return {
    oportunidadeNumero: doc.oportunidade?.numero ?? '',
    oportunidade: doc.oportunidade,

    trilha: p.trilha,
    cliente: doc.cliente,
    projeto: doc.projeto,
    validaAte: doc.validaAte,

    planoSuporte: p.planoSuporte,
    duracaoContratoMeses: p.duracaoContratoMeses,

    ptax: p.ptax,
    ptaxOrigem: p.ptaxOrigem,

    itensCusto: p.itensCusto,
    regiaoAws: p.regiaoAws,
    consumoManual: p.consumoManual,

    incluiCredito: p.credito !== null,
    creditoValorUsd: p.credito?.valorUsd ?? 0,
    creditoJanelaMeses: p.credito?.janelaMeses ?? 3,

    modalidadeDeploy: p.deploy.modalidade,
    deployCusto: p.deploy.custo,

    incluiBackup: p.licencasBackup !== null,
    backupFileServer: p.licencasBackup?.fileServer ?? 0,
    backupVirtualServer: p.licencasBackup?.virtualServer ?? 0,
    backupSharepoint: p.licencasBackup?.sharepoint ?? 0,
    backupVmwareHyperv: p.licencasBackup?.vmwareHyperv ?? 0,

    /*
     * Os padrões só valem quando a proposta não tem SafeStorage — inclusive no acervo emitido antes
     * do produto existir, em que o campo vem nulo. Reabrir uma dessas propostas não pode marcar o
     * produto sozinho, então `incluiSafeStorage` segue estritamente a premissa gravada.
     */
    incluiSafeStorage: p.safestorage !== null,
    safestoragePlano: p.safestorage?.plano ?? 'orbita',
    safestorageUsuarios: p.safestorage?.usuarios ?? 10,
    safestorageMensalidadeUsd: p.safestorage?.mensalidadeCustomizadaUsd ?? null,

    /*
     * Mesma regra do SafeStorage: proposta do acervo tem o campo nulo, e reabri-la não pode marcar o
     * produto sozinho. A linha em branco de cortesia só entra quando não há nenhuma gravada — se
     * entrasse sempre, o vendedor que salvasse sem mexer descartaria nada, mas veria um campo vazio
     * sugerindo que faltou preencher algo.
     */
    incluiCustosAdicionais: p.custosAdicionais !== null && p.custosAdicionais.length > 0,
    custosAdicionais:
      p.custosAdicionais !== null && p.custosAdicionais.length > 0
        ? p.custosAdicionais
        : [{ descricao: '', valorMensal: 0 }],

    /*
     * A escolha de tela é reconstruída da lista gravada. Com os dois focos, o seletor volta em
     * "ambos"; sem assessment, o seletor fica no padrão e o marcador desmarcado.
     */
    incluiAssessment: p.assessment !== null && p.assessment.length > 0,
    assessmentEscolha:
      p.assessment !== null && p.assessment.length > 1 ? 'ambos' : (p.assessment?.[0] ?? 'ambos'),
    /*
     * Zero no documento significa sem treinamento. O seletor volta em 1 nesse caso, para o marcador
     * abrir num estado válido se o vendedor decidir incluir agora.
     */
    incluiTreinamento: p.sessoesTreinamento > 0,
    sessoesTreinamento: p.sessoesTreinamento > 0 ? p.sessoesTreinamento : 1,

    // Conteúdo editorial
    badgeContexto: doc.conteudo.badgeContexto,
    promessaValor: doc.conteudo.promessaValor,
    subtituloHero: doc.conteudo.subtituloHero,
    observacoesHtml: doc.conteudo.observacoesHtml ?? '',

    arquiteturaDataUrl: '',
  };
}

export function TelaEdicao() {
  const { numero } = useParams<{ numero: string }>();
  const [estado, setEstado] = useState<'carregando' | 'erro' | 'pronto'>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [edicao, setEdicao] = useState<PropsModoEdicao | null>(null);

  const carregar = useCallback(async () => {
    if (numero === undefined) {
      setErro('Número da proposta não informado.');
      setEstado('erro');
      return;
    }

    setEstado('carregando');
    setErro(null);

    try {
      const r = await obterProposta(numero);

      if (!r.ok) {
        setErro(r.mensagem);
        setEstado('erro');
        return;
      }

      const { documento: doc, url } = r.proposta;

      setEdicao({
        numero: doc.numero,
        valoresIniciais: documentoParaValores(doc),
        urlPublica: url,
      });
      setEstado('pronto');
    } catch (e: unknown) {
      setErro(e instanceof Error ? e.message : 'Erro desconhecido ao carregar.');
      setEstado('erro');
    }
  }, [numero]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (estado === 'carregando') {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-sm text-gray-500">Carregando proposta {numero}...</p>
      </div>
    );
  }

  if (estado === 'erro' || edicao === null) {
    return (
      <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
        <h1 className="text-xl font-bold text-red-800">Não foi possível editar</h1>
        <p className="mt-2 text-sm text-red-700">{erro}</p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            type="button"
            onClick={() => void carregar()}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
          >
            Tentar novamente
          </button>
          <Link
            to="/propostas"
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50"
          >
            Voltar às propostas
          </Link>
        </div>
      </div>
    );
  }

  return <TelaConstrutor edicao={edicao} />;
}
